import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Share2, Check, Sparkles, Coffee, Megaphone, Users, ArrowRight, Loader2 } from 'lucide-react';
import { helpTiers } from '../siteContent';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NousAider() {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const onSubscribe = (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    if (!EMAIL_RE.test(trimmed) || trimmed.length > 120) {
      toast({ title: 'Email invalide', description: 'Veuillez saisir une adresse email valide.' });
      return;
    }
    window.location.assign(`/inscription?email=${encodeURIComponent(trimmed)}`);
  };

  const onCheckout = async (tier) => {
    if (!tier.lookup_key) return;
    setCheckoutLoading(tier.lookup_key);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: tier.lookup_key,
        quantity: 1,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.assign(data.checkout_url);
      } else {
        throw new Error('Aucune URL de paiement reçue');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Impossible de démarrer le paiement.';
      toast({ title: 'Erreur', description: typeof msg === 'string' ? msg : 'Erreur.' });
      setCheckoutLoading(null);
    }
  };

  return (
    <div>
      <section className="relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(250,204,21,0.18),transparent_55%)]" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-yellow-400/30 bg-yellow-400/5 text-yellow-300 text-xs font-medium tracking-wider uppercase">
            <Heart size={14} /> Soutenez le projet
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-yellow-400 leading-[1.05]">Nous aider</h1>
          <p className="mt-6 text-neutral-300 text-lg max-w-2xl mx-auto leading-relaxed">
            BuzzBump est un projet indépendant. Votre soutien nous aide à grandir, financer le développement et offrir la meilleure expérience possible à la ruche.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Share2, title: 'Partagez', desc: 'Faites connaître BuzzBump autour de vous, sur vos réseaux.' },
            { icon: Megaphone, title: 'Témoignez', desc: 'Donnez votre avis, partagez vos idées pour améliorer l\'app.' },
            { icon: Coffee, title: 'Contribuez', desc: 'Un soutien financier nous permet d\'avancer plus vite.' },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="p-7 rounded-2xl border border-white/10 bg-neutral-900/40 hover:border-yellow-400/30 transition-colors">
              <div className="h-12 w-12 grid place-items-center rounded-md bg-yellow-400/15 text-yellow-400 mb-5">
                <Icon size={22} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
              <p className="text-neutral-400 leading-relaxed text-sm">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-yellow-400 text-sm font-semibold tracking-wider uppercase mb-3">Devenez membre</div>
          <h2 className="text-4xl font-bold text-white">Choisissez votre niveau de soutien</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {helpTiers.map((t) => (
            <div
              key={t.name}
              className={`relative p-8 rounded-2xl border bg-neutral-900/40 transition-all duration-300 hover:-translate-y-1 ${
                t.popular ? 'border-yellow-400/60 shadow-lg shadow-yellow-400/10' : 'border-white/10 hover:border-yellow-400/30'
              }`}
            >
              {t.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-yellow-400 text-neutral-900 text-xs font-bold uppercase tracking-wider">
                  Populaire
                </div>
              )}
              <h3 className="text-2xl font-bold text-white mb-1">{t.name}</h3>
              <div className="text-4xl font-extrabold text-yellow-400 mb-6">{t.price}</div>
              <ul className="space-y-3 mb-7">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-neutral-300">
                    <Check size={16} className="text-yellow-400 mt-0.5 flex-shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => onCheckout(t)}
                disabled={checkoutLoading !== null}
                className={`w-full py-3 rounded-md font-semibold transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${
                  t.popular ? 'bg-yellow-400 text-neutral-900 hover:bg-yellow-300' : 'border border-white/15 text-white hover:border-yellow-400 hover:text-yellow-400'
                }`}
              >
                {checkoutLoading === t.lookup_key ? (
                  <><Loader2 size={16} className="animate-spin" /> Redirection...</>
                ) : (
                  <>Soutenir avec {t.price}</>
                )}
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
        <div className="relative overflow-hidden rounded-3xl border border-yellow-400/20 bg-gradient-to-br from-neutral-900 to-neutral-950 p-10 md:p-14">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-yellow-400/15 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-4 text-yellow-400">
                <Users size={18} />
                <span className="text-sm font-semibold uppercase tracking-wider">Newsletter</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">Restez dans la ruche</h2>
              <p className="mt-3 text-neutral-400">Recevez les actualités, les accès anticipés et les coulisses du projet.</p>
            </div>
            <form onSubmit={onSubscribe} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                maxLength={120}
                autoComplete="email"
                className="flex-1 px-4 py-3 rounded-md bg-neutral-950 border border-white/10 text-white placeholder-neutral-500 focus:outline-none focus:border-yellow-400/60 focus:ring-2 focus:ring-yellow-400/20 transition-colors"
              />
              <button type="submit" className="px-6 py-3 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors inline-flex items-center justify-center gap-2">
                Rejoindre <ArrowRight size={16} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
